<template>
  <v-card>
    <v-card-title>
      Print Products
      <v-spacer />
      <v-select
        v-model="filterId"
        outlined
        :items="filters"
        clearable
        label="Filter"
        hide-details
        class="min-width mr-2"
        dense
      />
      <v-select
        v-model="categoryId"
        outlined
        :items="categoriesToShow"
        clearable
        label="Category"
        hide-details
        class="min-width mr-2"
        item-value="id"
        item-text="name"
        dense
      />
      <v-icon @click="showAdd"> add </v-icon>
    </v-card-title>
    <v-divider />
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="items"
      disable-pagination
      hide-default-footer
      mobile-breakpoint="0"
      group-by="size"
    >
      <template #[`group.header`]="{ items, isOpen, toggle }">
        <th colspan="100%">
          <v-icon @click="toggle">
            {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
          </v-icon>
          {{ items[0].size }}
        </th>
      </template>
      <template #[`item.coreProducts`]="{ item }">
        <div
          v-for="(coreProduct, i) in item.coreProducts"
          :key="i"
        >
          {{ coreProduct.name }}
        </div>
      </template>
      <template #[`item.providerId`]="{ item }">
        {{ print_provider_name(item.providerId) }}
      </template>
      <template #[`item.syncDate`]="{ item }">
        {{ item.syncDate | normalDateWithTime }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          class="mr-2"
          :color="item.connections == 0 && item.matchesFound == 0 ? 'success' : 'info'"
          @click="addCoreProduct(item)"
        >
          <template v-if="item.connections == 0 && item.matchesFound == 0">
            Add Core Product
          </template>
          <template v-else> Edit Connections </template>
        </v-btn>
        <v-btn
          :to="`/print/products/${item.id}`"
          class="mr-2"
        >
          Edit
        </v-btn>
        <v-btn
          class="mr-2"
          outlined
          :loading="refreshing.includes(item.id)"
          @click="refresh(item.id)"
        >
          <v-icon class="mr-1">refresh</v-icon>
          Sync
        </v-btn>
        <v-btn
          color="error"
          @click="deleteProduct(item.id)"
        >
          Delete
        </v-btn>
      </template>
    </v-data-table>
    <LoadingSpinnerDialog :loading="loading_dialog" />
    <AddPrintProduct @save="loadData" />
    <EditCoreProducts
      ref="editCoreProducts"
      @save="loadData"
    />
    <AddCoreProduct
      @reload="loadData"
      @save="loadData"
    />
  </v-card>
</template>

<script>
import AddPrintProduct from '@/components/print/AddProduct.vue';
import EditCoreProducts from '@/components/print/EditCoreProducts';
import AddCoreProduct from '@/components/products/Add';
import { orderBy, sortBy, uniq } from 'lodash';
import { mapGetters } from 'vuex';
export default {
  name: 'PrintProducts',
  components: { AddPrintProduct, AddCoreProduct, EditCoreProducts },
  data: () => ({
    loading: true,
    loading_dialog: false,
    refreshing: [],
    headers: [
      { text: 'Size', value: 'size' },
      { text: 'Category', value: 'category' },
      { text: 'Provider', value: 'providerId' },
      { text: 'Material', value: 'material.display' },
      { text: 'Connections', value: 'connections' },
      { text: 'New Matches', value: 'matchesFound' },
      { text: 'Sync Date', value: 'syncDate' },
      { text: '', value: 'actions' }
    ],
    categoryId: null,
    filters: [
      { text: '*** None ***', value: 'none' },
      { text: 'New Matches Found', value: 'new' },
      { text: 'No Connection', value: 'connection' },
      { text: 'No Possible Connection', value: 'possible' }
    ],
    filterId: null
  }),
  computed: {
    items() {
      let list = this.list.map((x) => {
        var unitsLabel = this.getUnitsLabel(x.unitId);
        let measure1 = x.height;
        let measure2 = x.width;
        if (x.height > x.width) {
          measure1 = x.width;
          measure2 = x.height;
        }
        return Object.assign(x, {
          size: `${measure1}${unitsLabel} x ${measure2}${unitsLabel}`,
          connections: x.coreProducts.length,
          matchesFound: x.possibleCoreProducts.length
        });
      });
      if (this.categoryId) {
        if (this.categoryId === 'none') {
          list = list.filter((x) => !x.coreProducts.length);
        } else {
          list = list.filter(
            (x) => x.coreProducts.filter((y) => y.category.id === this.categoryId).length
          );
        }
      }

      if (this.filterId) {
        switch (this.filterId) {
          case 'new':
            list = list.filter((x) => x.matchesFound > 0);
            break;
          case 'connection':
            list = list.filter((x) => x.connections === 0);
            break;
          case 'possible':
            list = list.filter((x) => x.connections === 0 && x.matchesFound === 0);
            break;
        }
      }
      return orderBy(list, ['createdDate'], ['desc']);
    },
    categoriesToShow() {
      if (this.list && this.list.length) {
        const list = [{ id: 'none', name: '*** No Category ***' }];

        const categoryIds = uniq(
          this.list.flatMap((x) => x.coreProducts).map((x) => x.category.id)
        );

        return list.concat(
          sortBy(
            this.categories
              .filter((x) => categoryIds.includes(x.id))
              .map((x) => ({ id: x.id, name: x.name }))
          )
        );
      }
      return [];
    },
    ...mapGetters({
      categories: 'lookup/categories',
      list: 'print/list',
      print_provider_name: 'lookup/print_provider_name'
    })
  },
  created() {
    this.loadData(true);
  },
  methods: {
    refresh(id) {
      this.refreshing.push(id);
      this.$store
        .dispatch('print/refreshProduct', id)
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.refreshing = this.refreshing.filter((x) => x !== id);
        });
    },
    loadData(initial) {
      this.loading = true;
      const calls = [this.$store.dispatch('print/getAllProducts')];
      if (initial) {
        calls.push(this.$store.dispatch('lookup/getCategories'));
        calls.push(this.$store.dispatch('lookup/getProducts'));
        calls.push(this.$store.dispatch('print/getFourOverProducts'));
        calls.push(this.$store.dispatch('print/getLookups'));
      }
      Promise.all(calls)
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showAdd() {
      this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.ADD_PRINT_PRODUCT_SHOW, {});
    },
    addCoreProduct(item) {
      if (item.connections === 0 && item.matchesFound === 0) {
        this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.ADD_CORE_PRODUCT_SHOW, {
          height: item.height,
          width: item.width,
          printProductId: item.id
        });
      } else {
        this.$refs.editCoreProducts.show(item);
      }
    },
    async deleteProduct(id) {
      if (
        await this.$root.$confirm(
          'Delete Print Product',
          'Are you sure you want to delete this print product?',
          { color: 'error' }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch('print/deleteProduct', id)
          .then(() => {
            this.drawer = false;
            this.$root.$snackbarSuccess('Print Product deleted');
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>
